import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock, Mail, Building2, User, Phone, Briefcase, FileText, X } from 'lucide-react';
import { Card } from '@/components/ui/card';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [logo, setLogo] = useState(null);
  const [showRegister, setShowRegister] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  // Customer registration form state
  const [registerForm, setRegisterForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: '',
    business_type: '',
    business_name: '',
    registration_number: '',
    contact_name: '',
    contact_number: ''
  });

  // Forgot password state
  const [forgotEmail, setForgotEmail] = useState('');

  useEffect(() => {
    fetchLogo();
  }, []);

  const fetchLogo = async () => {
    try {
      const response = await axios.get(`${API}/public/settings/company`);
      if (response.data.website_logo) {
        setLogo(response.data.website_logo);
      }
    } catch (error) {
      console.error('Failed to load logo');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Try staff login first
      let response;
      let userType = 'staff';
      try {
        response = await axios.post(`${API}/auth/login`, {
          email,
          password
        });
        
        login(response.data.access_token, response.data.user);
        toast.success('Welcome back!');
        navigate('/dashboard');
      } catch (staffError) {
        // If staff login fails, try customer login
        try {
          response = await axios.post(`${API}/auth/customer-login`, {
            email,
            password
          });
          
          userType = 'customer';
          const userData = response.data.user;
          login(response.data.token, userData);
          toast.success('Welcome back!');
          
          // Navigation will be handled by App.js route guard
        } catch (customerError) {
          // Both failed, show error
          throw new Error(customerError.response?.data?.detail || 'Invalid email or password');
        }
      }
    } catch (error) {
      toast.error(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterChange = (e) => {
    setRegisterForm({
      ...registerForm,
      [e.target.name]: e.target.value
    });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validate passwords match
    if (registerForm.password !== registerForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    // Validate phone number
    if (!registerForm.contact_number.startsWith('7') && !registerForm.contact_number.startsWith('9')) {
      toast.error('Please enter mobile number for seamless communication. Number should start with 7 or 9.');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register-customer`, {
        first_name: registerForm.first_name,
        last_name: registerForm.last_name,
        email: registerForm.email,
        password: registerForm.password,
        business_type: registerForm.business_type,
        business_name: registerForm.business_name,
        registration_number: registerForm.registration_number || null,
        contact_name: registerForm.contact_name,
        contact_number: registerForm.contact_number
      });

      toast.success('Account created successfully! Please login.');
      setShowRegister(false);
      setEmail(registerForm.email);
      // Reset form
      setRegisterForm({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        confirm_password: '',
        business_type: '',
        business_name: '',
        registration_number: '',
        contact_name: '',
        contact_number: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: forgotEmail
      });

      // Check if development mode (reset_link returned)
      if (response.data.reset_link) {
        toast.success('Reset link generated! (Development Mode)', { duration: 8000 });
        // Show reset link in alert for development
        const resetUrl = response.data.reset_link;
        setTimeout(() => {
          if (window.confirm('Development Mode: Click OK to open reset link')) {
            window.location.href = resetUrl;
          }
        }, 500);
      } else {
        toast.success(response.data.message || 'Reset link has been sent to your email');
      }
      
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send reset link';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const businessTypes = [
    'Resort',
    'Guest House',
    'Safari',
    'Wholesale',
    'Retail',
    'Cafe & Restaurant',
    'SOE',
    'Gov'
  ];

  const showRegNumberField = registerForm.business_type && 
    !['SOE', 'Gov'].includes(registerForm.business_type);

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden">
      {/* Video Background */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
        data-testid="login-bg-video"
      >
        <source src="/login-bg.mp4" type="video/mp4" />
      </video>
      {/* Dark overlay for readability */}
      <div className="absolute inset-0 bg-black/40" />

      <div className="w-full max-w-md relative z-10">
        <div className="bg-white/15 backdrop-blur-2xl rounded-2xl shadow-2xl p-8 space-y-8 animate-slideIn border border-white/20">
          <div className="text-center space-y-2">
            {logo ? (
              <div className="inline-flex items-center justify-center mb-4">
                <img src={logo} alt="Company Logo" className="h-24 w-auto object-contain" />
              </div>
            ) : (
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl mb-4">
                <Building2 className="w-8 h-8 text-white" />
              </div>
            )}
            <h1 className="text-3xl font-bold text-white">Login</h1>
            <p className="text-white/70">Business Management Portal</p>
          </div>

          {!showRegister && !showForgotPassword && (
            <form onSubmit={handleLogin} className="space-y-6" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/90 font-medium">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="user@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-11 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-400"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-white/90 font-medium">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-11 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-400"
                    required
                    data-testid="password-input"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-emerald-300 hover:text-emerald-200 font-medium"
                >
                  Forgot Password?
                </button>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold rounded-lg shadow-lg shadow-emerald-500/30"
                data-testid="login-button"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setShowRegister(true)}
                  className="text-sm text-white/60 hover:text-emerald-300 font-medium"
                >
                  Don't have an account? <span className="text-emerald-300 font-semibold">Create Customer Account</span>
                </button>
              </div>
            </form>
          )}

          {showForgotPassword && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">Forgot Password</h2>
                <button onClick={() => setShowForgotPassword(false)} className="text-white/50 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-white/60">
                Enter your email address and we'll send you a link to reset your password.
              </p>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">Email Address</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="user@example.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="w-full text-sm text-white/60 hover:text-emerald-300"
                >
                  Back to Login
                </button>
              </form>
            </div>
          )}

          {showRegister && (
            <div className="space-y-6 max-h-[600px] overflow-y-auto pr-2">
              <div className="flex items-center justify-between sticky top-0 bg-transparent pb-4">
                <h2 className="text-2xl font-bold text-white">Create Account</h2>
                <button onClick={() => setShowRegister(false)} className="text-white/50 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleRegister} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      value={registerForm.first_name}
                      onChange={handleRegisterChange}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      value={registerForm.last_name}
                      onChange={handleRegisterChange}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reg_email">Email *</Label>
                  <Input
                    id="reg_email"
                    name="email"
                    type="email"
                    value={registerForm.email}
                    onChange={handleRegisterChange}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="reg_password">Password *</Label>
                    <Input
                      id="reg_password"
                      name="password"
                      type="password"
                      value={registerForm.password}
                      onChange={handleRegisterChange}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm_password">Re-enter Password *</Label>
                    <Input
                      id="confirm_password"
                      name="confirm_password"
                      type="password"
                      value={registerForm.confirm_password}
                      onChange={handleRegisterChange}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="business_type">Business *</Label>
                  <select
                    id="business_type"
                    name="business_type"
                    value={registerForm.business_type}
                    onChange={handleRegisterChange}
                    className="w-full h-10 px-3 rounded-md border border-white/20 bg-white/10 text-white focus:border-emerald-400 focus:outline-none"
                    required
                  >
                    <option value="">Select Business Type</option>
                    {businessTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="business_name">Business Name *</Label>
                  <Input
                    id="business_name"
                    name="business_name"
                    value={registerForm.business_name}
                    onChange={handleRegisterChange}
                    required
                  />
                </div>

                {showRegNumberField && (
                  <div className="space-y-2">
                    <Label htmlFor="registration_number">Registration Number</Label>
                    <Input
                      id="registration_number"
                      name="registration_number"
                      value={registerForm.registration_number}
                      onChange={handleRegisterChange}
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="contact_name">Contact Name *</Label>
                  <Input
                    id="contact_name"
                    name="contact_name"
                    value={registerForm.contact_name}
                    onChange={handleRegisterChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_number">Contact Number *</Label>
                  <Input
                    id="contact_number"
                    name="contact_number"
                    type="tel"
                    placeholder="7XXXXXXX or 9XXXXXXX"
                    value={registerForm.contact_number}
                    onChange={handleRegisterChange}
                    required
                  />
                  <p className="text-xs text-white/40">Number should start with 7 or 9</p>
                </div>

                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>

                <button
                  type="button"
                  onClick={() => setShowRegister(false)}
                  className="w-full text-sm text-white/60 hover:text-emerald-300"
                >
                  Already have an account? Login
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}